import { ExamineesSummary, ExamScheduleSummary } from '@exam-center/exam-event-examinees';
import {
  useExamEventDetails,
  DOWNLOADABLE_COURSE_STATUSES,
  DOWNLOADABLE_EXAM_TYPE,
  DownloadExamButton,
} from '@exam-center/exam-event-management';
import { ExamEventStatus } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { ProctorList, useProctoringDetailsModal } from '@exam-center/proctoring-details';
import { AddIcon, Alert, Button, Flex, PaperSection, Text } from '@shared/components';
import { PermissionGuard, WithPermission } from '@shared/permissions';
import { dayjs } from '@shared/utils';
import { useMemo } from 'react';

export default function ExamEventExamineesPage() {
  const { examEvent } = useExamEventDetails();
  const { setAction } = useProctoringDetailsModal();

  const allowedStatuses: ExamEventStatus[] = [
    ExamEventStatus.InProgress,
    ExamEventStatus.Scheduled,
    ExamEventStatus.ReadyToExam,
  ];

  const allowedToAddProctor = !!examEvent && allowedStatuses.includes(examEvent.status);

  const shouldDisplayDownloadExamButton = DOWNLOADABLE_EXAM_TYPE.includes(examEvent.examType);

  const disabledDownloadExamButtonState = useMemo(() => {
    const isExamEnded = dayjs().isAfter(dayjs(examEvent.examEndedAt));
    const isCourseAssignedOrAllocated = examEvent.courses.some((course) =>
      DOWNLOADABLE_COURSE_STATUSES.includes(course.status),
    );

    if (isExamEnded) {
      return { disabled: true, reason: 'EXAM_ENDED' as const };
    }
    if (!isCourseAssignedOrAllocated) {
      return { disabled: true, reason: 'NO_ASSIGNED_EXAM_PAPER' as const };
    }

    return { disabled: false };
  }, [examEvent]);

  const { disabled: isDownloadExamButtonDisabled, reason: disabledDownloadExamButtonReason } =
    disabledDownloadExamButtonState;

  return (
    <PermissionGuard requiredPermission={APP_PERMISSIONS.EXAM_EVENT_MANAGEMENT.EXAMINEES.VIEW}>
      <ExamScheduleSummary
        endAdornment={
          shouldDisplayDownloadExamButton ? (
            <DownloadExamButton
              examEventId={examEvent.id}
              disabled={isDownloadExamButtonDisabled}
              disabledReason={disabledDownloadExamButtonReason}
            />
          ) : null
        }
      />
      <PaperSection
        title='ผู้คุมสอบ'
        sx={{
          width: '100%',
          flexDirection: 'column',
          height: 'fit-content',
        }}
        endAdornment={
          <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE}>
            {allowedToAddProctor ? (
              <Button startIcon={<AddIcon />} onClick={() => setAction({ action: 'add-proctor' })}>
                เพิ่มผู้คุมสอบ
              </Button>
            ) : null}
          </WithPermission>
        }
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
            p: theme.spacing(6),
          })}
        >
          <Alert severity='info' sx={{ width: '100%', fontWeight: 400 }}>
            <Text>
              ศูนย์ทดสอบต้องมีเจ้าหน้าที่คุมสอบอย่างน้อย 2 คนต่อ 1 รอบสอบ
              และจัดเจ้าหน้าที่ประจำห้องสอบเพียงพอในอัตราส่วนเจ้าหน้าที่ 1 คนต่อผู้เข้าสอบ 40 คน{' '}
              <Text
                component='span'
                sx={{
                  textDecoration: 'underline',
                }}
              >
                เป็นไปตามมาตรฐานห้องสอบ
              </Text>
            </Text>
          </Alert>
          <ProctorList />
        </Flex>
      </PaperSection>
      <ExamineesSummary />
    </PermissionGuard>
  );
}
